import * as React from 'react';
import { CommandBar, Dialog, ICommandBarItemProps, Link, List, ProgressIndicator, TeachingBubble } from '@fluentui/react';
import { Map, LayersControl, FeatureGroup, Polyline, WMSTileLayer, Polygon, Tooltip, ScaleControl, Marker, GeoJSON, Rectangle } from 'react-leaflet';
import { IAssignmentDTO, IContainerSpot, ILayer, IMapData, IRoad, IStackMarker, ITextArea, IWorkingArea, MapDrawingColors, RoadType, WorkingAreaType } from '../../../models';
import { BackgroundMaps, MapLayers, MapUtilities } from '../../../helpers/Utilities';
import { WFSHelper } from '../../../helpers/WFSHelper';
import styles from './DocumentationDialog.module.scss';
import QRCode from "qrcode";
import L from "leaflet";
import { IAssignmentService, Services } from '../../../services';
import { IAssignmentFileDTO } from '../../../models/DTO/IAssignmentFileDTO';
import { AssignmentServiceProvider } from '../../../services/assignmentService';
import { generateDocumentation } from './GermanDocumentation';
import { LanguageContext } from '../../../LanguageContext';
import { getTreeSpeciesText } from '../Maps/Dialogs/stackDialogOptions';

export interface IGermanDocumentationDialogProps {
    currentItem: IAssignmentDTO;
    mapRef?: any;
    backgroundLayers?: ILayer[];
    layers?: ILayer[];
    wfsFeatures?: { [layerName: string]: any };
    t: (key: string) => string;
    onDismiss(): void;
    onSaved(newFiles: IAssignmentFileDTO[]): void;
}

export interface IGermanDocumentationDialogState {
    step: DocumentationSteps;
    mapWidth: number;
    mapData: IMapData;

    backgroundLayers: ILayer[];
    backgroundLayer: string;
    layers: ILayer[];
    wfsFeatures: { [layerName: string]: any };

    currentZoomLevel: number;
    workingOnIt: boolean;
    workingOnItText: string;
    saveButtonText: string;
    showStartInfo: boolean;
    showStep2Info: boolean;
    mapBounds: any;
    miniMapCenter: any;
    files: IAssignmentFileDTO[];
}

enum DocumentationSteps {
    Documentation = 1,
    OverviewMap = 2
}

export default class GermanDocumentationDialog extends React.Component<IGermanDocumentationDialogProps, IGermanDocumentationDialogState> {
    private minZoomLevelForLayers = 8;
    private minZoomLevelForDrawingObjects = 7;
    private lineOrBorderWeight = 9;
    private assignmentService: IAssignmentService;
    private mapRef: any;
    private miniMapRef: any;
    private groupRef: any;
    private qrCodeRef: React.RefObject<HTMLCanvasElement>;
    private boundHandleResize: () => void;
    static contextType = LanguageContext;
    context: React.ContextType<typeof LanguageContext>;

    constructor(props: IGermanDocumentationDialogProps) {
        super(props);
        this.assignmentService = Services.AssignmentService.Initialize(new AssignmentServiceProvider());
        this.qrCodeRef = React.createRef();
        this.boundHandleResize = this.handleResize.bind(this);

        MapUtilities.ConfigureLeafLet();

        let mapData = JSON.parse(props.currentItem.mapData + "") as IMapData;
        const backgroundLayers = props.backgroundLayers && props.backgroundLayers.length > 0
            ? props.backgroundLayers
            : [BackgroundMaps.DTK25Germany] as ILayer[];

        const layers = props.layers ? props.layers : [];
        // Enable only Documentation step layers initially
        const documentationLayerNames = ["Fauna Flora Habitat", "Vogelschutzgebiete"];
        for (const layer of layers) {
            layer.enabled = documentationLayerNames.includes(layer.name);
        }

        this.state = {
            step: DocumentationSteps.Documentation,
            mapWidth: this.getMapWidth(),
            currentZoomLevel: 13,
            mapData: mapData,
            backgroundLayers: backgroundLayers,
            backgroundLayer: "Luftbild",
            wfsFeatures: props.wfsFeatures || {},
            layers: layers,
            workingOnIt: false,
            workingOnItText: props.t('documentationDialog.generatingDocumentation'),
            saveButtonText: props.t('documentationDialog.saveDocumentation'),
            showStartInfo: true,
            showStep2Info: false,
            mapBounds: undefined,
            miniMapCenter: mapData.defaultPosition,
            files: []
        };
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.boundHandleResize);
        this.generateQRCode();
    }

    private onMapReady = () => {
        // Leaflet in a modal can render grey until size is invalidated after mount.
        if (this.mapRef && this.mapRef.leafletElement) {
            this.mapRef.leafletElement.invalidateSize();
            setTimeout(() => this.mapRef?.leafletElement?.invalidateSize(), 150);
        }

        const activeMapRef = this.mapRef || this.props.mapRef;
        WFSHelper.fetchWFSFeatures(activeMapRef, this.state.layers, (layerName, geoJSON) => {
            this.setState(prev => ({
                wfsFeatures: { ...prev.wfsFeatures, [layerName]: geoJSON }
            }));
        });

        this.setMapBounds();
    }

    private setMapBounds() {
        if (this.mapRef != null && this.groupRef != null) {
            const map = this.mapRef.leafletElement;
            const mapBounds = map.getBounds();
            const mapCenter = map.getCenter();

            this.setState({ mapBounds: mapBounds, miniMapCenter: mapCenter });
        }
    }

    public componentDidUpdate(prevProps: IGermanDocumentationDialogProps, prevState: IGermanDocumentationDialogState): void {
        if (prevState.mapData.fixedTaskPosition !== this.state.mapData.fixedTaskPosition) {
            this.generateQRCode();
        }

        if (prevState.step !== this.state.step && this.state.step === DocumentationSteps.OverviewMap) {
            this.generateQRCode();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.boundHandleResize);
    }

    private t(key: string): string {
        return this.props.t(key);
    }

    //region render
    public render(): React.ReactElement<IGermanDocumentationDialogProps> {
        const { t } = this.props;

        const CommandBar_Map_FarItems: ICommandBarItemProps[] = [
            {
                key: 'closeWithoutSaving',
                text: t('leafletMap.closeWithoutSaving'),
                iconProps: { iconName: 'ChromeClose' },
                onClick: () => { this.props.onDismiss(); }
            },
            {
                key: 'saveOrClose',
                text: this.state.saveButtonText,
                iconProps: { iconName: 'Save' },
                className: styles.buttonSaveAndClose,
                onClick: () => { this.saveDocumentation(); }
            }
        ];

        return (<React.Fragment>
            <Dialog
                hidden={false}
                modalProps={{ isBlocking: true }}
                minWidth={this.state.mapWidth}
                title={t('documentationDialog.title')}>
                <React.Fragment>
                    <CommandBar className={styles.commandBarMap} styles={{ root: { paddingLeft: "0px", height: "38px" } }} farItems={CommandBar_Map_FarItems} items={[]} />
                    <div id="documentationDialogOuter" className={styles.documentationDialog}>
                        <img className={styles.logo} src="/images/dalgas-logo.png?rev=1.0" />
                        <div className={styles.leftMenu}>
                            {this.renderLeftMenu()}
                        </div>
                        <div className={styles.seperator}></div>
                        <div className={styles.mapContainer}>
                            <div className={styles.stamData}>
                                <div><b>{t('documentationDialog.taskName')}:</b> {this.props.currentItem.orderName}</div>
                                <div>|</div>
                                <div><b>{t('assignmentPanel.dalgasAOLabel')}:</b> {this.props.currentItem.hdaoNo}</div>
                                <div>|</div>
                                <div style={{ paddingRight: "10px" }}>{t('documentationDialog.generatedOn')} {new Date().toLocaleDateString('da-DK')}</div>
                            </div>
                            {this.state.step == DocumentationSteps.OverviewMap && this.state.mapBounds != undefined &&
                                <div className={styles.miniMap}>
                                    <Map
                                        style={{ height: 200, width: 200 }}
                                        center={this.state.miniMapCenter}
                                        zoom={11}
                                        dragging={true}
                                        doubleClickZoom={false}
                                        scrollWheelZoom={true}
                                        attributionControl={false}
                                        zoomControl={false}>
                                        <WMSTileLayer
                                            layers={BackgroundMaps.DTK25Germany.layers}
                                            transparent={"TRUE" as any}
                                            styles={BackgroundMaps.DTK25Germany.styles ? BackgroundMaps.DTK25Germany.styles : ""}
                                            version={BackgroundMaps.DTK25Germany.version}
                                            format={'image/png'}
                                            url={BackgroundMaps.DTK25Germany.url} />
                                        <Rectangle bounds={this.state.mapBounds} pathOptions={{ weight: 1 }} />
                                    </Map>
                                </div>
                            }
                            <Map ondragend={() => { this.setMapBounds(); }} onzoomend={this.onZoomEnd.bind(this)} whenReady={this.onMapReady} zoomControl={true} ref={(ref: any) => { this.mapRef = ref; }} minZoom={7} maxZoom={13} bounceAtZoomLimits={true} doubleClickZoom={false} crs={MapUtilities.CRS} className={styles.map} center={this.state.mapData?.defaultPosition} zoom={this.state.mapData?.recentZoomLevel} scrollWheelZoom={true}>
                                <ScaleControl imperial={false} />
                                <LayersControl collapsed={false} position="topright">
                                    {this.state.backgroundLayers.map((layer: ILayer, idx) =>
                                        <LayersControl.BaseLayer key={`background-${idx}`} name={layer.name} checked={this.state.backgroundLayer === layer.name}>
                                            <WMSTileLayer
                                                layers={layer.layers}
                                                transparent={layer.transparent as any}
                                                styles={layer.styles ? layer.styles : ""}
                                                version={layer.version || "1.3.0"}
                                                format={'image/png'}
                                                url={layer.url}
                                            />
                                        </LayersControl.BaseLayer>
                                    )}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForLayers && this.state.layers.map((layer: ILayer, idx) => {
                                        if (layer.type === "wms") {
                                            return (
                                                <LayersControl.Overlay key={`overlay-${layer.name}`} name={layer.name} checked={layer.enabled}>
                                                    <WMSTileLayer
                                                        layers={layer.layers}
                                                        transparent={layer.transparent as any}
                                                        format={'image/png'}
                                                        styles={layer.styles ? layer.styles : ""}
                                                        url={layer.url}
                                                    />
                                                </LayersControl.Overlay>
                                            );
                                        } else if (layer.type === "wfs") {
                                            const layerData = this.state.wfsFeatures[layer.name];
                                            return (
                                                <LayersControl.Overlay key={`overlay-${layer.name}`} name={layer.name} checked={layer.enabled}>
                                                    {layerData ? (
                                                        <GeoJSON data={layerData} style={(feature: any) => ({
                                                            color: feature?.properties?._wfsFeatureColor || "#d32f2f",
                                                            weight: 2,
                                                            fillColor: feature?.properties?._wfsFeatureColor || "#ff8a80",
                                                            fillOpacity: 0.35
                                                        })} onEachFeature={WFSHelper.bindWFSPopup} />
                                                    ) : (
                                                        <FeatureGroup />
                                                    )}
                                                </LayersControl.Overlay>
                                            );
                                        }
                                        return null;
                                    })}
                                </LayersControl>
                                <FeatureGroup ref={(ref: any) => { this.groupRef = ref; this.setMapBounds(); }}>
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.fixedTaskPosition && this.renderFixedTaskPosition()}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.stacks?.map((stack: IStackMarker) => this.renderStack(stack))}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.workingAreas?.map((workingarea: IWorkingArea) => this.renderWorkingArea(workingarea))}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.roads?.map((road: IRoad) => this.renderRoad(road))}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.containerSpots?.map((marker: IContainerSpot) => this.renderContainerSpot(marker))}
                                    {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.textAreas?.map((textArea: ITextArea) => this.renderTextArea(textArea))}
                                </FeatureGroup>
                            </Map>
                        </div>
                    </div>
                    {this.state.showStartInfo &&
                        <React.Fragment>
                            <div id="bubbleCenterPosition" className={styles.bubbleCenterPosition}>

                            </div>
                            <TeachingBubble
                                target={`#bubbleCenterPosition`}
                                primaryButtonProps={{
                                    children: t('documentationDialog.understood'),
                                    onClick: () => { this.setState({ showStartInfo: false }); },
                                }}
                                secondaryButtonProps={{
                                    children: t('documentationDialog.goToOverviewMap'),
                                    onClick: () => { this.goToStep2([] as IAssignmentFileDTO[]); },
                                }}
                                hasSmallHeadline={true}
                                hasCloseButton={false}
                                headline={t('documentationDialog.step1Title')}
                            >
                                {t('documentationDialog.step1Description')}
                            </TeachingBubble>
                        </React.Fragment>
                    }
                    {this.state.showStep2Info &&
                        <React.Fragment>
                            <div id="bubbleCenterPosition" className={styles.bubbleCenterPosition}>

                            </div>
                            <TeachingBubble
                                target={`#bubbleCenterPosition`}
                                primaryButtonProps={{
                                    children: t('documentationDialog.understood'),
                                    onClick: () => { this.setState({ showStep2Info: false }); },
                                }}
                                hasSmallHeadline={true}
                                hasCloseButton={false}
                                headline={t('documentationDialog.step2Title')}
                            >
                                {t('documentationDialog.step2Description')}
                            </TeachingBubble>
                        </React.Fragment>
                    }
                </React.Fragment>
            </Dialog>
            {this.state.workingOnIt &&
                <Dialog minWidth={500} title={t('documentationDialog.pleaseWait')} hidden={false} modalProps={{ isBlocking: true }}>
                    <ProgressIndicator label={this.state.workingOnItText} description={t('documentationDialog.autoCloseMessage')} />
                </Dialog>
            }
        </React.Fragment>);
    }

    private goToStep2(newFiles: IAssignmentFileDTO[]) {
        let layers = [...this.state.layers];

        // Enable only Overview Map step layers
        const overviewLayerNames = [MapLayers.Wendemoeglichkeiten.name, MapLayers.Forstwege.name, MapLayers.Rettungspunkte.name];
        layers.forEach((part, index, theArray) => {
            layers[index].enabled = overviewLayerNames.includes(part.name);
        });

        this.setState({
            backgroundLayer: "DTK25 Germany",
            layers: layers,
            files: newFiles,
            workingOnIt: false,
            showStartInfo: false,
            showStep2Info: true,
            saveButtonText: this.t('documentationDialog.saveOverview'),
            step: DocumentationSteps.OverviewMap
        });
    }

    private async saveDocumentation() {
        const t = this.t.bind(this);

        switch (this.state.step) {
            case DocumentationSteps.Documentation:
                {
                    this.setState({ workingOnIt: true, workingOnItText: t('documentationDialog.generatingDocumentation') }, async () => {
                        try {
                            const pdfDataUri = await generateDocumentation(
                                this.mapRef || this.props.mapRef,
                                this.state.layers,
                                this.state.wfsFeatures
                            );

                            if (!pdfDataUri) {
                                alert(t('documentationDialog.errorGenerateDocumentation'));
                                this.props.onDismiss();
                                return;
                            }

                            const newFiles = [
                                {
                                    name: this.createFileName(t('documentationDialog.documentationPdf')),
                                    type: "Documentation",
                                    content: pdfDataUri
                                }
                            ] as IAssignmentFileDTO[];

                            const result = await this.assignmentService.UploadDocumentation({
                                assignmentId: this.props.currentItem.id,
                                files: newFiles
                            });

                            if (!result) {
                                alert(t('documentationDialog.errorUploadDocumentation'));
                                this.props.onDismiss();
                            } else {
                                this.goToStep2(newFiles);
                            }
                        } catch (error) {
                            console.error('Error generating documentation:', error);
                            alert(t('documentationDialog.errorGenerateDocumentation'));
                            this.props.onDismiss();
                        }
                    });
                }
                break;
            case DocumentationSteps.OverviewMap:
                {
                    this.setState({ workingOnIt: true, workingOnItText: t('documentationDialog.generatingOverviewMap') }, async () => {
                        try {
                            const pdfDataUri = await generateDocumentation(
                                this.mapRef || this.props.mapRef,
                                this.state.layers,
                                this.state.wfsFeatures
                            );

                            if (!pdfDataUri) {
                                alert(t('documentationDialog.errorGenerateDocumentation'));
                                this.props.onDismiss();
                                return;
                            }

                            let allFiles = this.state.files;

                            allFiles.forEach((part, index, theArray) => {
                                allFiles[index].KeepDuringSoftDelete = true;
                                allFiles[index].content = undefined; // clear content to reduce size in http post
                            });

                            allFiles.push({
                                name: this.createFileName(t('documentationDialog.overviewMapPdf')),
                                type: "Documentation",
                                content: pdfDataUri
                            } as IAssignmentFileDTO);

                            const result = await this.assignmentService.UploadDocumentation({
                                assignmentId: this.props.currentItem.id,
                                files: allFiles
                            });

                            if (!result) {
                                alert(t('documentationDialog.errorUploadDocumentation'));
                                this.props.onDismiss();
                            } else {
                                this.props.onSaved(allFiles);
                            }
                        } catch (error) {
                            console.error('Error generating overview map:', error);
                            alert(t('documentationDialog.errorGenerateDocumentation'));
                            this.props.onDismiss();
                        }
                    });
                }
                break;
        }
    }

    private createFileName(filename: string) {
        return `ID${this.props.currentItem.hdaoNo == null ? "" : this.props.currentItem.hdaoNo} ${this.props.currentItem.orderName} - ${filename}`;
    }

    //region render leftmenu
    private renderLeftMenu(): JSX.Element {
        const t = this.t.bind(this);

        return <React.Fragment>
            <div className={styles.leftMenuHeading}>{t('documentationDialog.legend')}</div>
            <div className={styles.leftMenuItem}>
                <div className={styles.signaturDrawingObjects} >
                    <div data-is-focusable={true} title={t('documentationDialog.taskGPSPoint')}>
                        <div>{t('documentationDialog.taskGPSPoint')}</div>
                        <img src="images/marker-icon-gold.png" />
                    </div>
                    {this.state.step == DocumentationSteps.Documentation &&
                        <div data-is-focusable={true} title={t('documentationDialog.stack')}>
                            <div>{t('documentationDialog.stack')}</div>
                            <img src="images/HD-Icon-Stack.png" />
                        </div>
                    }
                    <div data-is-focusable={true} title={t('documentationDialog.containerSpot')}>
                        <div>{t('documentationDialog.containerSpot')}</div>
                        <img src="images/HD-Icon-Containerspot.png" />
                    </div>
                </div>
            </div>
            <div className={styles.leftMenuItem} >
                <div data-is-focusable={true} title={t('documentationDialog.roadAllowedForbidden')}>
                    <div style={{ display: "flex" }}>
                        <div style={{ width: "50%" }}>{t('documentationDialog.roadAllowed')}</div>
                        <div style={{ width: "50%" }}>{t('documentationDialog.roadForbidden')}</div>
                    </div>
                    <div style={{ display: "flex" }}>
                        <div style={{ backgroundColor: MapDrawingColors.RoadAllowed, width: "50%", height: "10px" }} />
                        <div style={{ backgroundColor: MapDrawingColors.RoadProhibited, width: "50%", height: "10px" }} />
                    </div>
                </div>
            </div>
            {this.state.step == DocumentationSteps.Documentation &&
                <React.Fragment>
                    <div className={styles.leftMenuHeading}>{t('documentationDialog.workingAreasAndLines')}</div>
                    {this.state.mapData.workingAreas?.map((v, i, a) => {
                        return <div key={`wa_${i}`} style={{ color: v.color == "transparent" ? "red" : v.color }} >{v.type == WorkingAreaType.WorkingArea ? t('documentationDialog.workingArea') : t('documentationDialog.line')}{v.name}</div>
                    })}
                </React.Fragment>
            }
            {this.state.step == DocumentationSteps.OverviewMap && this.state.mapData.fixedTaskPosition &&
                <React.Fragment>
                    <div className={styles.leftMenuHeading}>{t('documentationDialog.stacks')}</div>
                    {this.state.mapData.stacks?.map((v, i, a) => {
                        return <div style={{ marginBottom: "5px" }} key={`stack_${i}`}><img src="images/HD-Icon-Stack.png" /> {v.no}, {v.size} {t('common.rm')} {getTreeSpeciesText(v.treeSpecices, t)}</div>
                    })}
                    <div className={styles.leftMenuHeading}>{t('documentationDialog.taskGPSPoint')}</div>
                    <Link
                        title={t('documentationDialog.openInGoogleMaps')}
                        target="_blank"
                        href={`https://www.google.com/maps/search/?api=1&query=${this.state.mapData.fixedTaskPosition.position?.lat},${this.state.mapData.fixedTaskPosition.position?.lng}`}>
                        <div>{t('documentationDialog.coordinate')}: {this.state.mapData.fixedTaskPosition.position?.lat},{this.state.mapData.fixedTaskPosition.position?.lng}</div>
                        {this.renderQRCode()}
                    </Link>
                </React.Fragment>
            }
            <List items={this.getSortedLegendLayers()} onRenderCell={this.onRenderLayerListItem.bind(this)} />
        </React.Fragment>
    }

    private getSortedLegendLayers(): ILayer[] {
        const legendOrder = [MapLayers.Rettungspunkte.name, MapLayers.Forstwege.name, MapLayers.Wendemoeglichkeiten.name];

        return this.state.layers
            .filter(d => d.enabled)
            .slice()
            .sort((a, b) => legendOrder.indexOf(a.name) - legendOrder.indexOf(b.name));
    }

    private onRenderLayerListItem(item?: ILayer, index?: number, isScrolling?: boolean): JSX.Element {
        // For WFS layers, show one swatch per visible area since each area can have its own color
        if (item?.type === 'wfs') {
            const legendEntries = WFSHelper.getVisibleFeatureLegendEntries(this.mapRef, item?.name || '', this.state.wfsFeatures);

            // No areas of this layer are currently shown on the map, so skip it in the legend
            if (legendEntries.length === 0) {
                return <React.Fragment />;
            }

            return (
                <div className={styles.leftMenuItem} >
                    <div data-is-focusable={true} title={item?.name + ""}>
                        <div>{item?.name + ''}</div>
                        {legendEntries.map((entry, entryIndex) => (
                            <div key={`${item?.name}_${entryIndex}`} style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "2px" }}>
                                <div style={{ backgroundColor: entry.color, width: "40px", height: "10px", border: "1px solid #ccc", flexShrink: 0 }}></div>
                                <span>{entry.name || `Område ${entryIndex + 1}`}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (item?.name === 'Forstwege' && item?.legendUrl) {
            return (
                <div className={styles.leftMenuItem} data-pdf-fullpage-legend={item.name}>
                    <div data-is-focusable={true} title={item?.name + ""}>
                        <div>{item?.name + ''}</div>
                        <img src={item.legendUrl} alt={item.name} style={{ maxWidth: "220px" }} />
                    </div>
                </div>
            );
        }

        if (item?.name === 'Wendemoeglichkeiten' && item?.legendUrl) {
            return (
                <div className={styles.leftMenuItem} data-pdf-fullpage-legend={item.name}>
                    <div data-is-focusable={true} title={item?.name + ""}>
                        <div>{item?.name + ''}</div>
                        <img src={item.legendUrl} alt={item.name} style={{ maxWidth: "220px" }} />
                    </div>
                </div>
            );
        }

        if (item?.name === 'Rettungspunkte' && item?.legendUrl) {
            return (
                <div className={styles.leftMenuItem} >
                    <div data-is-focusable={true} title={item?.name + ""}>
                        <div>{item?.name + ''}</div>
                        <img src={item.legendUrl} alt={item.name} style={{ maxWidth: "220px" }} />
                    </div>
                </div>
            );
        }

        // For WMS layers, show signature image if available
        return (
            <div className={styles.leftMenuItem} >
                <div data-is-focusable={true} title={item?.name + ""}>
                    <div>{item?.name + ''}</div>
                    {item?.signature && <img src={`/images/${item.signature}`} />}
                </div>
            </div>
        );
    }

    private renderFixedTaskPosition(): JSX.Element {
        if (!this.state.mapData.fixedTaskPosition) {
            return <React.Fragment />
        }

        return <Marker icon={MapUtilities.CreateFixedTaskPositionIcon(this.state.currentZoomLevel)} attribution={`fixedTaskPosition`} key={`fixedTaskPosition`} position={this.state.mapData.fixedTaskPosition.position} />
    }

    private renderStack(stack: IStackMarker): JSX.Element {
        const t = this.t.bind(this);

        let icon: L.Icon<L.IconOptions> = MapUtilities.CreateStackIcon(this.state.currentZoomLevel, stack.tooltipPosition);

        return <Marker icon={icon} attribution={stack.identifier} key={stack.identifier} position={stack.position}>
            <Tooltip permanent={true} direction={stack.tooltipPosition ?? "top"} opacity={0.5}>
                <div>
                    <div>{t('leafletMap.stackNo')}: {stack.no}</div>
                    {this.state.step == DocumentationSteps.Documentation &&
                        <React.Fragment>
                            <div>{stack.size} {t('common.rm')}</div>
                            <div>{getTreeSpeciesText(stack.treeSpecices, t)}</div>
                        </React.Fragment>
                    }
                </div>
            </Tooltip>
        </Marker>
    }

    private renderWorkingArea(workingarea: IWorkingArea): JSX.Element {
        if (workingarea.type == WorkingAreaType.WorkingArea) {
            return <Polygon weight={this.lineOrBorderWeight} attribution={workingarea.identifier} key={workingarea.identifier} positions={workingarea.coordinates} opacity={0.7} fillColor={workingarea.color} color={workingarea.color == "transparent" ? "red" : workingarea.color} />
        } else {
            return <Polyline weight={this.lineOrBorderWeight} attribution={workingarea.identifier} key={workingarea.identifier} positions={workingarea.coordinates} opacity={0.7} fillColor={workingarea.color} color={workingarea.color == "transparent" ? "red" : workingarea.color} />
        }
    }

    private renderRoad(road: IRoad): JSX.Element {
        return <Polyline attribution={road.identifier} weight={this.lineOrBorderWeight} key={road.identifier} positions={road.coordinates} opacity={0.7} color={road.type == RoadType.Allowed ? MapDrawingColors.RoadAllowed : MapDrawingColors.RoadProhibited} />
    }

    private renderContainerSpot(containerSpot: IContainerSpot): JSX.Element {
        return <Marker icon={MapUtilities.CreateContainerspotIcon(this.state.currentZoomLevel)} attribution={containerSpot.identifier} key={containerSpot.identifier} position={containerSpot.position} />
    }

    private renderTextArea(textArea: ITextArea): JSX.Element {
        return <Marker icon={MapUtilities.CreateTextIcon(`<div style="display:inline-block; color:#fff; padding:5px; white-space:nowrap; opacity: 0.7; background-color:${textArea.color}">${textArea.name}</div>`)} attribution={textArea.identifier} key={textArea.identifier} position={textArea.position} />
    }

    private onZoomEnd() {
        if (this.mapRef && this.mapRef.leafletElement) {
            var currentZoom = this.mapRef.leafletElement.getZoom();
            this.setState({ currentZoomLevel: currentZoom });
        }
        this.setMapBounds();
    }

    private getMapWidth() {
        return window.innerWidth * 0.95;
    }

    private handleResize() {
        this.setState({ mapWidth: this.getMapWidth() });
    }

    private generateQRCode() {
        if (this.qrCodeRef.current && this.state.mapData.fixedTaskPosition) {
            const url = `https://www.google.com/maps/search/?api=1&query=${this.state.mapData.fixedTaskPosition.position?.lat},${this.state.mapData.fixedTaskPosition.position?.lng}`;
            QRCode.toCanvas(this.qrCodeRef.current, url, { width: 140 }, (error) => {
                if (error) console.error('QR Code generation error:', error);
            });
        }
    }

    private renderQRCode(): JSX.Element {
        return <canvas ref={this.qrCodeRef} />;
    }
}
